import { describeRefusal } from "%/helpers/member-post-refusal";
import { defineStore } from "pinia";
import { useGeneralStore } from "%/stores/general-store";
import { $axios } from "@bloglovin/vue-framework";
import dayjs from "dayjs";
import SmartFeedEngine from "SmartFeed/smart-feed-engine";
import { PUBLISHING_PLATFORM_TYPE } from "%/constants/story-constants";
import { useSocialNetworksStore } from "@/stores/social-networks-store";
import { useConfigurationStore } from "%/stores/configuration-store";
import { usePostFeedStore } from "SmartFeed/stores/feed/post-feed-store";
import { useMemberPostFeedStore } from "SmartFeed/stores/feed/member-post-feed-store";
import { insertMemberPostIntoFeed } from "%/dynamic-component-editor/standard-components/base-story-card/member-post-feed-actions";
import { useOrgPostFeedStore } from "SmartFeed/stores/feed/org-post-feed-store";
import { useSyndicatedStoryFeedStore } from "SmartFeed/stores/feed/my-syndicated-story-feed-store";
import { useMyPublicationPostFeedStore } from "SmartFeed/stores/feed/my-publication-post-feed-store";
import {
  STORY_PUBLISH_STATUS_ACTIVE,
  STORY_PUBLISH_STATUS_DRAFT,
  STORY_PUBLISH_STATUS_NEEDS_EDITING,
  STORY_PUBLISH_STATUS_PUBLISH_FAILED,
  STORY_PUBLISH_STATUS_SCHEDULED,
  STORY_PUBLISH_STATUS_SCHEDULED_NEEDS_REVIEW,
  STORY_PUBLISH_STATUS_SUBMITTED_FOR_REVIEW,
} from "~/constants/story-status-constants";

// Social-network publishing runs in a background worker; the editor polls the story's publish
// status until it lands. Mirrors the Studio / FanWeb stores' pollPublishStatus cadence.
const SOCIAL_NETWORK_PUBLISH_POLL_INTERVAL_MS = 2000;

export const useStoryStore = defineStore("story-store", {
  state: () => ({
    quickPostRefusal: null,
    story: {},
    stories: [],
    isLoadingCreateStory: false,
    isLoadingCopyStoryToPublication: false,
    isLoadingPublishSyndicationStory: false,
    uploadStoryThumbnailLoading: false,
    uploadStoryImageLoading: false,
    uploadStoryVideoLoading: false,
    uploadStoryAudioLoading: false,
    loadingAddHashtagToStory: false,
    errorAddHashtagToStory: false,
    updateStoryExclusivityIsLoading: false,
    updateStoryRequiresSyndicationApprovalContentIsLoading: false,
    updateStoryAllowRemixContentIsLoading: false,
    updateStoryTitleIsLoading: false,
    updateStoryAssignToPlatformIsLoading: false,
    updateStoryIsLoading: false,
    updatePostIsLoading: false,
    isCreatingStoryVariant: false,
    updateStoryIsLoadingId: undefined,
    isLikeActionLoading: false,
    isVotingActionLoading: false,
    assignToPublishingPlatformModalOpen: false,
    assignToSyndicatedPublishingPlatformModalOpen: false,
    assignToExclusivePublishingPlatformModalOpen: false,
    isPublishingStory: false,
    isUnpublishingStory: false,
    forcePublishAfterAssigning: false,
    isSyndicatingActionLoading: false,
    socialNetworkPublishPollTimeoutId: null,
    socialNetworkPublishError: "",
  }),
  getters: {
    orgId() {
      return useGeneralStore().orgId;
    },
    userId() {
      return useGeneralStore().userId;
    },
    isUserLoggedIn() {
      return useConfigurationStore().isSignupAndLoginEnabled && this.userId > 0;
    },
    fanUrl() {
      return useGeneralStore().fanUrl;
    },
    storySegments() {
      const segmentArray = Object.values(this.story.segments);
      return this.story.segment_order.reduce((segmentItem, order) => {
        const segment = segmentArray.find(segmentData => segmentData.story_segment_id === segmentItem.story_segment_id);
        return [
          ...order,
          segment,
        ];
      }, []);
    },
  },
  actions: {
    // Resolve a media-backed video segment ({ mediaId }) to its playback URL —
    // the HLS master playlist once transcoding is ready, the source file while
    // it's still in flight. Display-only: segments keep storing { mediaId }.
    resolveVideoPlayback(mediaId) {
      return new Promise((resolve, reject) => {
        if (!mediaId) {
          reject(new Error("resolveVideoPlayback requires a mediaId"));
          return;
        }
        this.apiGet(
          `/media/${mediaId}`,
          {},
          data => resolve({
            url: data.url || "",
            isHls: Boolean(data.isHls),
            mediaType: data.mediaType || "",
            thumbnailUrl: data.thumbnailUrl || "",
          }),
          () => reject(new Error("Could not resolve video playback")),
        );
      });
    },
    saveKeyframeThumbnail(mediaId, atSec) {
      return new Promise((resolve, reject) => {
        if (!mediaId) {
          reject(new Error("saveKeyframeThumbnail requires a mediaId"));
          return;
        }
        this.apiPost(
          `/media/${mediaId}/thumbnail/keyframe`,
          { at_sec: Math.max(0, Math.floor(atSec || 0)), org_id: this.orgId },
          data => resolve(data.thumbnail_media_id || 0),
          () => reject(new Error("Could not save the keyframe as a thumbnail")),
        );
      });
    },
    setMediaThumbnail(mediaId, thumbnailMediaId) {
      return new Promise((resolve, reject) => {
        if (!mediaId || !thumbnailMediaId) {
          reject(new Error("setMediaThumbnail requires a mediaId and a thumbnailMediaId"));
          return;
        }
        this.apiPost(
          `/media/${mediaId}/thumbnail`,
          { thumbnail_media_id: thumbnailMediaId, org_id: this.orgId },
          () => resolve(thumbnailMediaId),
          () => reject(new Error("Could not set the media thumbnail")),
        );
      });
    },
    triggerStoryAssignModal() {
      if (this.story.publishing_platform_id && this.story.publishing_platform_id > 0) {
        return;
      }
      this.assignToPublishingPlatformModalOpen = true;
    },
    triggerSyndicatedAssignModal() {
      this.assignToSyndicatedPublishingPlatformModalOpen = true;
    },
    triggerExclusiveAssignModal() {
      this.assignToExclusivePublishingPlatformModalOpen = true;
    },
    publishingStory(redirectAfterPublish = true) {
      if (!this.story.publishing_platform_id && this.story.publishing_platform_type === "") {
        this.triggerStoryAssignModal();
        this.forcePublishAfterAssigning = true;
        return false;
      }
      this.isPublishingStory = true;
      switch (this.story.publishing_platform_type) {
        case PUBLISHING_PLATFORM_TYPE.PUBLICATION:
        case PUBLISHING_PLATFORM_TYPE.SYNDICATION_MARKETPLACE:
          this.publishStory(this.story.story_id, () => {
            this.navigateAfterPublishing(redirectAfterPublish);
          });
          break;
        case PUBLISHING_PLATFORM_TYPE.SOCIAL_NETWORK:
          this.stopSocialNetworkPublishPolling();
          this.socialNetworkPublishError = "";
          useSocialNetworksStore().postMessage(
            this.orgId,
            this.story.publishing_platform_id,
            this.story.story_id,
            data => {
              // The POST only enqueues the job; poll its status so the editor reflects the real
              // outcome instead of navigating away fire-and-forget (the old bug).
              const jobId = Number(data?.social_network_publish_job_id || 0);
              if (!jobId) {
                this.failSocialNetworkPublish("Failed to start publishing.");
                return;
              }
              this.pollSocialNetworkPublishStatus(jobId, redirectAfterPublish);
            },
            () => {
              this.failSocialNetworkPublish("Failed to start publishing.");
            },
          );
          break;
        case PUBLISHING_PLATFORM_TYPE.EMAIL:
          this.isPublishingStory = false;
          break;
        default:
          this.isPublishingStory = false;
          break;
      }
    },
    submitForReviewStory(redirectAfterPublish = true) {
      if (!this.story.publishing_platform_id && this.story.publishing_platform_type === "") {
        this.triggerStoryAssignModal();
        this.forcePublishAfterAssigning = true;
        return false;
      }
      this.isPublishingStory = true;
      switch (this.story.publishing_platform_type) {
        case PUBLISHING_PLATFORM_TYPE.PUBLICATION:
        case PUBLISHING_PLATFORM_TYPE.SYNDICATION_MARKETPLACE:
          this.submitStoryForReview(this.story.story_id, () => {
            this.navigateAfterPublishing(redirectAfterPublish, STORY_PUBLISH_STATUS_SUBMITTED_FOR_REVIEW);
          });
          break;
        case PUBLISHING_PLATFORM_TYPE.SOCIAL_NETWORK:
        case PUBLISHING_PLATFORM_TYPE.EMAIL:
        default:
          this.isPublishingStory = false;
          break;
      }
    },
    schedulePublishingStory(dateTime, redirectAfterPublish = true) {
      if (!this.story.publishing_platform_id && this.story.publishing_platform_type === "") {
        this.triggerStoryAssignModal();
        this.forcePublishAfterAssigning = true;
        return false;
      }
      this.isPublishingStory = true;
      switch (this.story.publishing_platform_type) {
        case PUBLISHING_PLATFORM_TYPE.PUBLICATION:
        case PUBLISHING_PLATFORM_TYPE.SYNDICATION_MARKETPLACE:
          this.scheduleStory(this.story.story_id, dateTime, () => {
            this.navigateAfterPublishing(redirectAfterPublish, STORY_PUBLISH_STATUS_SCHEDULED);
          });
          break;
        case PUBLISHING_PLATFORM_TYPE.SOCIAL_NETWORK:
        case PUBLISHING_PLATFORM_TYPE.EMAIL:
        default:
          this.isPublishingStory = false;
          break;
      }
    },
    schedulePublishingStoryNeedsReview(dateTime, redirectAfterPublish = true) {
      if (!this.story.publishing_platform_id && this.story.publishing_platform_type === "") {
        this.triggerStoryAssignModal();
        this.forcePublishAfterAssigning = true;
        return false;
      }
      this.isPublishingStory = true;
      switch (this.story.publishing_platform_type) {
        case PUBLISHING_PLATFORM_TYPE.PUBLICATION:
        case PUBLISHING_PLATFORM_TYPE.SYNDICATION_MARKETPLACE:
          this.scheduleStoryNeedsReview(this.story.story_id, dateTime, () => {
            this.navigateAfterPublishing(redirectAfterPublish, STORY_PUBLISH_STATUS_SCHEDULED_NEEDS_REVIEW);
          });
          break;
        case PUBLISHING_PLATFORM_TYPE.SOCIAL_NETWORK:
        case PUBLISHING_PLATFORM_TYPE.EMAIL:
        default:
          this.isPublishingStory = false;
          break;
      }
    },
    rejectPublishingStoryAfterReview(redirectAfterPublish = true) {
      if (!this.story.publishing_platform_id && this.story.publishing_platform_type === "") {
        this.triggerStoryAssignModal();
        this.forcePublishAfterAssigning = true;
        return false;
      }
      this.isPublishingStory = true;
      switch (this.story.publishing_platform_type) {
        case PUBLISHING_PLATFORM_TYPE.PUBLICATION:
        case PUBLISHING_PLATFORM_TYPE.SYNDICATION_MARKETPLACE:
          this.rejectStoryAfterReview(this.story.story_id, () => {
            this.navigateAfterPublishing(redirectAfterPublish, STORY_PUBLISH_STATUS_NEEDS_EDITING);
          });
          break;
        case PUBLISHING_PLATFORM_TYPE.SOCIAL_NETWORK:
        case PUBLISHING_PLATFORM_TYPE.EMAIL:
        default:
          this.isPublishingStory = false;
          break;
      }
    },
    publishStoryFromMarketplace() {
      if (this.story.is_exclusive === 1) {
        this.triggerExclusiveAssignModal();
        return;
      }
      this.triggerSyndicatedAssignModal();
    },
    navigateAfterPublishing(redirectAfterPublish = true, statusType = "active") {
      if (!redirectAfterPublish) {
        useOrgPostFeedStore().reload();
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const redirect = urlParams.get("redirect");

      if (redirect) {
        window.location.href = `${window.location.origin}${redirect}`;
        return;
      }

      this.story.status = statusType;
    },
    pollSocialNetworkPublishStatus(jobId, redirectAfterPublish = true) {
      this.apiGet(
        `/story/social-network-publish-job/${jobId}/status`,
        {},
        data => {
          const status = data?.status || "";

          if (status === "complete") {
            this.stopSocialNetworkPublishPolling();
            this.isPublishingStory = false;
            this.navigateAfterPublishing(redirectAfterPublish, STORY_PUBLISH_STATUS_ACTIVE);
            return;
          }

          if (status === "failed") {
            this.failSocialNetworkPublish(data?.errorMessage || "Publishing failed.");
            return;
          }

          this.socialNetworkPublishPollTimeoutId = window.setTimeout(() => {
            this.pollSocialNetworkPublishStatus(jobId, redirectAfterPublish);
          }, SOCIAL_NETWORK_PUBLISH_POLL_INTERVAL_MS);
        },
        () => {
          this.failSocialNetworkPublish("Failed to check publishing status.");
        },
      );
    },
    failSocialNetworkPublish(errorMessage) {
      this.stopSocialNetworkPublishPolling();
      this.isPublishingStory = false;
      this.socialNetworkPublishError = errorMessage;
      this.story.status = STORY_PUBLISH_STATUS_PUBLISH_FAILED;
    },
    stopSocialNetworkPublishPolling() {
      if (this.socialNetworkPublishPollTimeoutId) {
        clearTimeout(this.socialNetworkPublishPollTimeoutId);
        this.socialNetworkPublishPollTimeoutId = null;
      }
    },
    createStory(platform = {}, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isLoadingCreateStory = true;
      this.apiPost("/story/create",
        {
          org_id: this.orgId,
          user_id: this.userId,
          publishing_platform_id: platform.publishing_platform_id || 0,
          publishing_platform_type: platform.publishing_platform_type ?? null,
          variant_type: platform.variant_type ?? "",
        },
        response => {
          window.location.href = `/stories/editor?story_id=${response.story_id}`;
          successHandler(response);
        },
        () => {
          errorHandler();
        },
        () => {
          this.isLoadingCreateStory = false;
        });
    },
    copyStoryToPublication(storyId, publicationId, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isLoadingCopyStoryToPublication = true;
      this.apiPost(`/story/${storyId}/copy-to-publication`,
        {
          publication_id: publicationId,
        },
        response => {
          successHandler(response);
        },
        () => {
          errorHandler();
        },
        () => {
          this.isLoadingCopyStoryToPublication = false;
        });
    },
    saveSessionDataAndRedirect(postData) {
      sessionStorage.setItem("postCreationData", JSON.stringify(postData));
      const encodedCurrentUri = encodeURIComponent(window.location.pathname);
      window.location.href = `${this.fanUrl}/login?redirect=${encodedCurrentUri}`;

    },
    createQuickStory(publicationId, postData, callback) {
      if (!this.isUserLoggedIn) {
        this.saveSessionDataAndRedirect(postData);
      }
      this.updatePostIsLoading = true;
      const data = {
        publication_id: publicationId,
        org_id: this.orgId,
        title: postData.title,
        segment: {
          "style": "BrandedParagraphElement",
          "data": {
            ...postData.content,
          },
          "editorId": Math.random().toString(36).substring(2, 15),
        },
        image_segment: postData.image_segment ?? {},
      };

      if (postData.image) {
        data.image_segment = {
          ...postData.image,
          "editorId": Math.random().toString(36).substring(2, 15),
        };
      }
      this.apiPost(`/publication/${publicationId}/quick-story`, {
        data,
      }, response => {
        usePostFeedStore().reload();
        useMemberPostFeedStore().reloadMemberPosts();
        insertMemberPostIntoFeed(publicationId, response?.story_id);
        this.updatePostIsLoading = false;
        this.quickPostRefusal = null;
        callback(true);
      }, error => {
        this.updatePostIsLoading = false;
        this.quickPostRefusal = describeRefusal(error);
        callback(false, this.quickPostRefusal);
      });
    },
    generateTextStoryContent(prompt, successCallback) {
      return this.apiPost("/story/generate/text", { prompt }, successCallback);
    },
    generateAiProductCollection(prompt, successCallback, errorCallback = () => {}) {
      return this.apiPost("/story/generate/products", { prompt }, successCallback, errorCallback);
    },
    generateTextStoryContentChanges(prompt, content, successCallback) {
      return this.apiPost("/story/generate/text/edit", { prompt, content }, successCallback);
    },
    updateStory(storyId, data, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.updateStoryIsLoading = true;
      this.updateStoryIsLoadingId = storyId;

      this.apiPost(`/story/${storyId}`, {
        "story_version_id": this.story.current_story_version_id,
        "live_segment_order": data.liveSegmentOrder,
        "segments": data.segments,
      }, result => {
        this.story = {
          ...this.story,
          ...result,
        };
        this.updateStoryIsLoading = false;
        if (successHandler) {
          successHandler();
          return;
        }
        window.location.href = "/stories";
      }, () => {
        errorHandler();
      }, () => {
        this.updateStoryIsLoading = false;
        this.updateStoryIsLoadingId = undefined;
      });
    },
    removeStoryThumbnail(storyId, responseHandler = () => {}, finallyHandler = () => {}) {
      this.uploadStoryThumbnailLoading = true;
      this.apiPost(`/story/${storyId}/thumbnail/remove`, {}, response => {
        this.story.thumbnail = response.thumbnail;
        responseHandler(response);
      }, () => {}, () => {
        this.uploadStoryThumbnailLoading = false;
        finallyHandler();
      });
    },
    async uploadStoryThumbnail(storyId, selectedFile) {
      this.uploadStoryThumbnailLoading = true;
      const formData = new FormData();
      formData.append("org_id", this.orgId);
      formData.append("image", selectedFile);

      return await $axios
          .post(`/story/${storyId}/thumbnail`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(response => {
            this.story.thumbnail = response.data.thumbnail;
            return response;
          })
          .finally(() => {
            this.uploadStoryThumbnailLoading = false;
          });
    },
    async uploadStoryImage(selectedFile) {
      this.uploadStoryImageIsLoading = true;
      const formData = new FormData();
      formData.append("org_id", this.orgId);
      formData.append("image", selectedFile);

      return await $axios
          .post("/story/image/upload", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(response => {
            return response.data.imageUrl;
          })
          .finally(() => {
            this.uploadStoryImageIsLoading = false;
          });
    },
    async uploadStoryVideo(selectedFile) {
      this.uploadStoryVideoLoading = true;

      const formData = new FormData();
      formData.append("org_id", this.orgId);
      formData.append("video", selectedFile);

      return await $axios
          .post("/story/video/upload", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(response => {
            return {
              videoUrl: response.data.videoUrl,
              posterUrl: response.data.posterUrl,
            };
          })
          .finally(() => {
            this.uploadStoryVideoLoading = false;
          });
    },
    async uploadStoryAudio(selectedFile) {
      this.uploadStoryAudioLoading = true;

      const formData = new FormData();
      formData.append("org_id", this.orgId);
      formData.append("audio", selectedFile);

      return await $axios
          .post("/story/audio/upload", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(response => {
            return response.data.audioUrl;
          })
          .finally(() => {
            this.uploadStoryAudioLoading = false;
          });
    },
    addHashtagsToStory(hashtags, storyId, errorHandler = () => {
    }, successHandler = () => {
    }) {
      this.loadingAddHashtagToStory = true;
      return $axios.post(`/story/${storyId}/tag`,
        {
          hashtags,
        }).then(response => {
        this.story.timestamp_updated = dayjs().unix();
        this.story.hashtags = response.data.hashtags;
        successHandler();
      }).catch(() => {
        errorHandler();
      }).finally(() => {
        this.loadingAddHashtagToStory = false;
      });
    },
    removeHashtagFromStory(hashtagId, storyId, errorHandler = () => {
    }, successHandler = () => {
    }) {
      this.loadingAddHashtagToStory = true;
      return $axios.post(
        `/story/${storyId}/untag`,
        {
          hashtag_id: hashtagId,
        },
      ).then(response => {
        this.story.timestamp_updated = dayjs().unix();
        this.story.hashtags = response.data.hashtags;
        successHandler();
      }).catch(() => {
        errorHandler();
      }).finally(() => {
        this.loadingAddHashtagToStory = false;
      });
    },
    updateStoryExclusiveContentFlag(storyId, exclusive, successHandler = () => {
    }, errorHandler) {
      this.updateStoryExclusivityIsLoading = true;
      this.apiPost(`/story/${storyId}/exclusive`, {
        exclusive: exclusive ? 1 : 0,
      }, () => {
        this.story.exclusive = exclusive;
        this.story.timestamp_updated = dayjs().unix();
        this.updateStoryExclusivityIsLoading = false;
        successHandler(true);
      }, () => {
        errorHandler(false);
      }, () => {
        this.updateStoryExclusivityIsLoading = false;
      });
    },
    updateStoryAllowRemixContentFlag(storyId, allowRemixContent, successHandler = () => {
    }, errorHandler) {
      this.updateStoryAllowRemixContentIsLoading = true;
      this.apiPost(`/story/${storyId}/allow-remix`, {
        allow_remix: allowRemixContent ? 1 : 0,
      }, data => {
        this.story.allow_remix = allowRemixContent;
        this.story.timestamp_updated = data.timestamp_updated;
        successHandler(true);
      }, () => {
        errorHandler(false);
      }, () => {
        this.updateStoryAllowRemixContentIsLoading = false;
      });
    },
    updateStoryRequiresSyndicationApprovalContentFlag(storyId, requiresSyndicationApproval, successHandler = () => {
    }, errorHandler) {
      this.updateStoryRequiresSyndicationApprovalContentIsLoading = true;
      this.apiPost(`/story/${storyId}/requires-syndication-approval`, {
        requires_syndication_approval: requiresSyndicationApproval ? 1 : 0,
      }, data => {
        this.story.requires_syndication_approval = requiresSyndicationApproval;
        this.story.timestamp_updated = data.timestamp_updated;
        successHandler(true);
      }, () => {
        errorHandler(false);
      }, () => {
        this.updateStoryRequiresSyndicationApprovalContentIsLoading = false;
      });
    },
    updateStoryTitle(storyId, storyTitle, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.updateStoryTitleIsLoading = true;
      this.apiPost(`/story/${storyId}/title`, {
        story_title: storyTitle,
      }, data => {
        this.story.title = data.title;
        this.story.timestamp_updated = data.timestamp_updated;
        this.story.status = data.status;
        successHandler();
      }, () => {
        errorHandler();
      }, () => {
        this.updateStoryTitleIsLoading = false;
      });
    },
    assignStoryToPublishingPlatform(storyId, platformType, platformId, variant = "", successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.updateStoryAssignToPlatformIsLoading = true;
      this.apiPost(`/story/${storyId}/platform`, {
        publishing_platform_type: platformType,
        publishing_platform_id: platformId,
        variant_type: variant,
      }, data => {
        this.story.publishing_platform_type = platformType;
        this.story.publishing_platform_id = platformId;
        this.story.timestamp_updated = data.timestamp_updated;
        successHandler();
      }, () => {
        errorHandler();
      }, () => {
        this.updateStoryAssignToPlatformIsLoading = false;
      });
    },
    assignSyndicatedStoryToPublishingPlatform(storyId, platformType, platformId, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.updateStoryAssignToPlatformIsLoading = true;
      this.apiPost(`/story/${storyId}/platform`, {
        publishing_platform_type: platformType,
        publishing_platform_id: platformId,
      }, data => {
        this.story.publishing_platform_type = platformType;
        this.story.publishing_platform_id = platformId;
        this.story.timestamp_updated = data.timestamp_updated;
        successHandler();
      }, () => {
        errorHandler();
      }, () => {
        this.updateStoryAssignToPlatformIsLoading = false;
      });
    },
    createStoryVariant(storyId, variant, platformId, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isCreatingStoryVariant = true;

      this.apiPost(`/story/${storyId}/variant`,
        {
          variant,
          platform_id: platformId,
        },
        data => {
          this.isCreatingStoryVariant = false;
          successHandler(data);
        },
        () => {
          this.isCreatingStoryVariant = false;
          errorHandler();
        });
    },
    publishStory(storyId, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isPublishingStory = true;
      this.story.status = STORY_PUBLISH_STATUS_ACTIVE;
      this.apiPost(`/story/${storyId}/publish`, {}, response => {
        successHandler(response);
      }, () => {
        errorHandler();
      }, () => {
        this.isPublishingStory = false;
      });
    },
    submitStoryForReview(storyId, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isPublishingStory = true;
      this.story.status = STORY_PUBLISH_STATUS_SUBMITTED_FOR_REVIEW;
      this.apiPost(`/story/${storyId}/submit/review`, {}, response => {
        successHandler(response);
      }, () => {
        errorHandler();
      }, () => {
        this.isPublishingStory = false;
      });
    },
    scheduleStory(storyId, dateTime, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isPublishingStory = true;
      this.apiPost(`/story/${storyId}/${dateTime}/schedule/publish`, {}, response => {
        successHandler(response);
      }, () => {
        errorHandler();
      }, () => {
        this.isPublishingStory = false;
      });
    },
    scheduleStoryNeedsReview(storyId, dateTime, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isPublishingStory = true;
      this.apiPost(`/story/${storyId}/${dateTime}/review/schedule`, {}, response => {
        successHandler(response);
      }, () => {
        errorHandler();
      }, () => {
        this.isPublishingStory = false;
      });
    },
    rejectStoryAfterReview(storyId, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isPublishingStory = true;
      this.apiPost(`/story/${storyId}/review/reject`, {}, response => {
        successHandler(response);
      }, () => {
        errorHandler();
      }, () => {
        this.isPublishingStory = false;
      });
    },
    unpublishStory(storyId, updateStatus) {
      this.isUnpublishingStory = true;
      this.apiPost(`/story/${storyId}/unpublish`, {}, () => {
        if (updateStatus) {
          this.story.status = STORY_PUBLISH_STATUS_DRAFT;
        }
      }, () => {
      }, () => {
        this.isUnpublishingStory = false;
        useOrgPostFeedStore().reload();
      });
    },
    async likeStory(storyId) {
      this.isLikeActionLoading = true;
      await this.apiPost(
        `/story/${storyId}/like`,
        {},
        response => {
          this.isLikeActionLoading = false;
          new SmartFeedEngine().addEntities(response.entities);
          this.story.like_status = response.entities.communityPost[storyId].likeStatus;
          this.story.num_likes = response.entities.communityPost[storyId].numLikes;
        },
        () => {
        },
        () => {
          this.isLikeActionLoading = false;
        },
      );
    },
    async unlikeStory(storyId) {
      this.isLikeActionLoading = true;
      await this.apiPost(
        `/story/${storyId}/unlike`,
        {},
        response => {
          this.isLikeActionLoading = false;
          new SmartFeedEngine().addEntities(response.entities);
          // intentionally not reloading this store so the UI does not abruptly change
          // to give the user a chance to undo the action in case it was done on accident
          // useLovedPostFeedStore().reload();
          this.story.like_status = response.entities.communityPost[storyId].likeStatus;
          this.story.num_likes = response.entities.communityPost[storyId].numLikes;
        },
        () => {
          this.isLikeActionLoading = false;
        },
      );
    },
    async upVoteStory(storyId) {
      this.isVotingActionLoading = true;
      await this.apiPost(
        `/story/${storyId}/upvote`,
        {},
        response => {
          this.isVotingActionLoading = false;
          new SmartFeedEngine().addEntities(response.entities);
          this.story.vote_status = response.entities.communityPost[storyId].voteStatus;
          this.story.vote_score = response.entities.communityPost[storyId].voteScore;
        },
        () => {
        },
        () => {
          this.isVotingActionLoading = false;
        },
      );
    },
    async downVoteStory(storyId) {
      this.isVotingActionLoading = true;
      await this.apiPost(
        `/story/${storyId}/downvote`,
        {},
        response => {
          this.isVotingActionLoading = false;
          new SmartFeedEngine().addEntities(response.entities);
          this.story.vote_status = response.entities.communityPost[storyId].voteStatus;
          this.story.vote_score = response.entities.communityPost[storyId].voteScore;
        },
        () => {
        },
        () => {
          this.isVotingActionLoading = false;
        },
      );
    },
    removeVoteStory(storyId) {
      this.isVotingActionLoading = true;
      return this.apiPost(
        `/story/${storyId}/remove-vote`,
        {},
        response => {
          this.isVotingActionLoading = false;
          new SmartFeedEngine().addEntities(response.entities);
          this.story.vote_status = response.entities.communityPost[storyId].voteStatus;
          this.story.vote_score = response.entities.communityPost[storyId].voteScore;
        },
        () => {
        },
        () => {
          this.isVotingActionLoading = false;
        },
      );
    },
    publishSyndicationStory(sourceStoryId, publicationId) {
      this.isLoadingPublishSyndicationStory = true;
      return this.apiPost(
        `/story/${sourceStoryId}/syndication/publish`,
        {
          "publication_id": publicationId,
        },
        response => {
          this.isLoadingPublishSyndicationStory = false;
          this.assignStoryToPublishingPlatform(
            response.story_id,
            response.publishing_platform_type,
            response.publishing_platform_id,
            "",
            () => {
              if (window.location.href.includes("syndicated-story")) {
                window.location.href = `${window.location.origin}/publication/${publicationId}/stories`;
              }
            },
          );
        },
        () => {
        },
        () => {
          this.isLoadingPublishSyndicationStory = false;
        },
      );
    },
    approveSyndicatedStory(storyId) {
      this.isSyndicatingActionLoading = true;
      return this.apiPost(
        `/story/${storyId}/syndication/approve`,
        {
          "story_id": storyId,
        },
        () => {
          this.isSyndicatingActionLoading = false;
          useSyndicatedStoryFeedStore().reload();
        },
        () => {
        },
        () => {
          this.isSyndicatingActionLoading = false;
        },
      );
    },
    rejectSyndicatedStory(storyId) {
      this.isSyndicatingActionLoading = true;
      return this.apiPost(
        `/story/${storyId}/syndication/reject`,
        {
          "story_id": storyId,
        },
        () => {
          this.isSyndicatingActionLoading = false;
          useSyndicatedStoryFeedStore().reload();
        },
        () => {
        },
        () => {
          this.isSyndicatingActionLoading = false;
        },
      );
    },
    deleteStory(storyId) {
      this.apiPost(`/story/${storyId}/delete`, {}, () => {
        useMyPublicationPostFeedStore().reload();
      }, () => {
      });
    },
  },
});
